import { useEffect, useState, lazy, Suspense } from "react";
import { Numpad } from "../components/stunt/numpad";

// Lazy load the Stripe payment form (client-only)
const StripePaymentForm = lazy(() => import("../components/stripe-payment-form"));

// Success screen component
function SuccessScreen({ amount }) {
  return (
    <div className="flex flex-col items-center justify-center text-center">
      <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-500/10">
        <svg
          className="h-10 w-10 text-green-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
      <h1 className="mb-2 font-bold text-3xl">Thank You!</h1>
      <p className="mb-8 text-muted-foreground">
        Your payment of <span className="font-semibold">${amount}</span> was successful.
      </p>
      <a
        href="/"
        className="rounded-lg bg-primary px-8 py-3 font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
      >
        Return Home
      </a>
    </div>
  );
}

// Loading spinner for Stripe form
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center p-8">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-border border-t-primary" />
    </div>
  );
}

export default function PayRoute() {
  const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || null;
  const [amount, setAmount] = useState("0");
  const [clientSecret, setClientSecret] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [successAmount, setSuccessAmount] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Check for success redirect from Stripe
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get("success");
    const returnedAmount = urlParams.get("amount");

    if (success === "true" && returnedAmount) {
      setPaymentSuccess(true);
      setSuccessAmount(returnedAmount);
      // Clean up URL
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const formatAmount = (value) => {
    const parts = value.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  };

  const handleKeyPress = (key) => {
    if (key === "." && amount.includes(".")) return;
    // Limit decimal places to 2
    if (amount.includes(".")) {
      const decimalPart = amount.split(".")[1];
      if (decimalPart && decimalPart.length >= 2) return;
    }
    if (amount === "0" && key !== ".") {
      setAmount(key);
    } else {
      setAmount(amount + key);
    }
  };

  const handleDelete = () => {
    setAmount((prev) => (prev.length > 1 ? prev.slice(0, -1) : "0"));
  };

  const handlePayClick = async () => {
    setError(null);
    setIsLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/api/stripe/payment-intent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ amount }),
      });

      const data = await response.json();

      if (data.ok && data.clientSecret) {
        setClientSecret(data.clientSecret);
        setShowPaymentForm(true);
        setError(null);
      } else if (data.errorMessage) {
        setError(data.errorMessage);
      }
    } catch (err) {
      setError("Failed to create payment intent");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowPaymentForm(false);
    setClientSecret(null);
  };

  // Show success screen
  if (paymentSuccess) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-background text-foreground">
        <SuccessScreen amount={successAmount} />
      </div>
    );
  }

  // Show payment form with Stripe Elements (client-only)
  if (showPaymentForm && clientSecret && stripePublishableKey) {
    return (
      <div style={{
        display: 'flex',
        height: '100vh',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#000000ff',
        padding: '16px'
      }}>
        <div style={{ marginBottom: '32px', textAlign: 'center' }}>
          <div style={{ marginBottom: '16px' }}>
            <img
              src="/trappay_logo.png"
              alt="TrapPay"
              style={{
                height: '70px',
                width: 'auto',
                filter: 'drop-shadow(0 10px 30px rgba(34, 197, 94, 0.4))',
                animation: 'float 3s ease-in-out infinite'
              }}
            />
          </div>

          <style>{`
            @keyframes float {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-10px); }
            }
          `}</style>
          <div style={{
            fontWeight: 'bold',
            fontSize: '48px',
            letterSpacing: '-0.025em',
            color: 'white'
          }}>
            ${formatAmount(amount)}
          </div>
        </div>

        <Suspense fallback={<LoadingSpinner />}>
          <StripePaymentForm
            clientSecret={clientSecret}
            stripePublishableKey={stripePublishableKey}
            amount={amount}
            onCancel={handleCancel}
          />
        </Suspense>
      </div>
    );
  }

  // Show numpad for amount entry
  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#000000ff',
      overflow: 'hidden',
      padding: '16px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '320px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
          <img
            src="/trappay_logo.png"
            alt="TrapPay"
            style={{
              height: '80px',
              width: 'auto',
              filter: 'drop-shadow(0 10px 30px rgba(34, 197, 94, 0.4))',
              animation: 'float 3s ease-in-out infinite'
            }}
          />
        </div>

        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
        `}</style>

        <div style={{
          marginBottom: '40px',
          fontWeight: 'bold',
          fontSize: '72px',
          letterSpacing: '-0.025em',
          color: 'white'
        }}>
          ${formatAmount(amount)}
        </div>

        {error && (
          <div style={{
            marginBottom: '16px',
            width: '100%',
            padding: '8px 16px',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            color: '#ef4444',
            fontSize: '14px',
            borderRadius: '8px'
          }}>
            {error}
          </div>
        )}

        <div style={{ width: '100%' }}>
          <Numpad
            onKeyPress={handleKeyPress}
            onDelete={handleDelete}
            onSubmit={handlePayClick}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
}
